import React, { useState, useEffect } from 'react';
import { Paper, Box, Typography, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { axiosRun } from "../utils/axiosWrap";
import { config } from "../config";
import { grey } from '@mui/material/colors';

const ApplicantRequestProcessing = () => {
    const [documentData, setDocumentData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const { hash } = useParams();

    useEffect(() => {
        const fetchDocumentData = async () => {
            try {
                setIsLoading(true);
                const data = await axiosRun(config.apiLinks.getLinksForDocuments.replace('%hash%', hash), 'GET');
                setDocumentData(data || {});
                setError(null);
            } catch (error) {
                console.error('Помилка отримання даних документа:', error);
                setError(t('applicant.documentNotFound'));
            } finally {
                setIsLoading(false);
            }
        };
        fetchDocumentData();
        const intervalId = setInterval(fetchDocumentData, 90 * 1000);
        return () => clearInterval(intervalId);
    }, [hash, t]);

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setError(null);
    }
    const startShare = async (link4sharing, internalRowId) => {
        try {
            await axiosRun(config.apiLinks.updateDocumentStatus, 'POST', { internalRowId, hash, isSend: 'sended' });
            window.location.href = link4sharing;
        } catch (error) {
            console.error('Error updating document status:', error);
        }
    };

    return (
        <Box sx={{ margin: { xs: '0 9%', sm: '0 30%' }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{ backgroundColor: 'success.main', padding: 2, borderRadius: 1, maxWidth: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <img
                    src="https://snau.edu.ua/wp-content/themes/smartuniversity/assets/images/default/logo.png"
                    alt="University Logo"
                    style={{ maxWidth: '150px', borderRadius: '50%', objectFit: 'contain', clipPath: 'circle()' }}
                />
                <Typography variant="body1" sx={{ color: 'white', marginTop: 1, textAlign: 'center' }}>
                    {t('applicant.SNAU_NAME_TEXT')}
                </Typography>
            </Box>
            <Alert severity="info" sx={{ width: '100%', maxWidth: '400px', backgroundColor: 'primary.main', color: 'white', '& .MuiAlert-icon': { color: 'white' }, mx: 2 }}>
                {t('applicant.infoForApplicantHowToUseSharing')}
            </Alert>
            {isLoading ? (
                <CircularProgress />
            ) : error ? (
                <Paper elevation={3} sx={{ padding: 2, backgroundColor: 'white', width: '100%', maxWidth: '400px' }}>
                    <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>
                        {error}
                    </Typography>
                </Paper>
            ) : Object.keys(documentData).length === 0 ? (
                <Paper elevation={3} sx={{ padding: 2, backgroundColor: 'white', width: '100%', maxWidth: '400px' }}>
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        {t('applicant.noDocuments')}
                    </Typography>
                </Paper>
            ) : (
                Object.keys(documentData).map((key) => {
                    const documentDataItem = documentData[key];
                    return (
                        <Paper
                            key={documentDataItem.documentCode || key}
                            elevation={3}
                            sx={{ padding: 2, backgroundColor: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%', maxWidth: '400px' }}
                        >

                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Alert
                                    severity={documentDataItem.isSend !== 'not send' ? 'success' : 'error'}
                                    sx={{ padding: '2px 8px', fontSize: '0.875rem', minWidth: '120px', textAlign: 'center' }}
                                >
                                    {t(`applicant.send_status_${documentDataItem.isSend}`)}
                                </Alert>
                                <Alert
                                    severity={documentDataItem.isDone ? 'success' : 'error'}
                                    sx={{ padding: '2px 8px', fontSize: '0.875rem', minWidth: '120px', textAlign: 'center' }}
                                >
                                    {t(documentDataItem.isDone ? 'applicant.received' : 'applicant.notReceived')}
                                </Alert>
                            </Box>
                            <Typography variant="h6" sx={{ textAlign: 'center' }}>
                                {currentLanguage === 'en' ? documentDataItem.nameEn : documentDataItem.nameUa}
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => startShare(documentDataItem.link4sharing, documentDataItem.internalRowId)}
                                target="_blank"
                                disabled={!documentDataItem.link4sharing}
                                startIcon={
                                    <img
                                        src="/dii.png"
                                        alt="Share icon"
                                        style={{ width: 48, height: 48, marginRight: '15px' }}
                                    />
                                }
                                sx={{
                                    backgroundColor: grey[200],
                                    color: '#000', // Чорний текст для контрасту
                                    borderRadius: '12px', // Більш заокруглені кути
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: grey[300], // Трохи темніший відтінок при наведенні
                                    },
                                    '&:disabled': {
                                        backgroundColor: grey[400], // Сірий для неактивної кнопки
                                    },
                                }}
                            >
                                {t('applicant.startSharing')}
                            </Button>
                        </Paper>
                    );
                })
            )}
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={handleSnackbarClose} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ApplicantRequestProcessing;